import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { map, mergeMap, switchMap } from 'rxjs/operators';
import { Partner } from '../models/partner';
import { PartnerService } from './partner.service';
import { LoginStateService } from '../../login/services/login-state.service';
import { PartnerStateEnum } from './partner-state.enum';
import { Router } from '@angular/router';
import { PasswordGenerator } from '../../../helpers/password-generator';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class PartnerStateService {

  private _partner: Partner = null;
  private _selectedChild: Partner = null;
  private _state: PartnerStateEnum = PartnerStateEnum.Reset;

  constructor(
    private partnerService: PartnerService,
    private loginStateService: LoginStateService,
    private router: Router,
    private translateService: TranslateService,
  ) {

    loginStateService.eventUserLoggedIn
      .pipe(
        mergeMap(_ => this._loggedInPartner()),
      )
      .subscribe();

    loginStateService.eventUserLoggedOut.subscribe(
      () => {
        this._partner = null;
      }
    );
  }

  reload(newPartner: Partner): void {
    this._partner = newPartner;
  }

  public initialize(): Observable<Partner> {
    return this._loggedInPartner();
  }

  private _loggedInPartner(): Observable<Partner> {
    return this.partnerService.loggedInPartner.pipe(
      switchMap(
        partner => {
          this._partner = partner;
          return this._reloadChildren().pipe(map(_ => partner));
        }),
    );
  }

  reloadPartner(): Observable<Partner> {
    return this._loggedInPartner();
  }

  get loggedInPartner(): Partner {
    return this._partner;
  }

  get state(): PartnerStateEnum {
    return this._state;
  }

  get selectedChild(): Partner {
    return this._selectedChild;
  }

  createPartner(
    partner: Partner,
    password: string,
    sendEmail: boolean = true,
  ): Observable<boolean> {

    return of(true).pipe(
      switchMap(_ => {
        if (!password) {
          password = PasswordGenerator.generate();
        }

        if (partner && !partner.lang) {
          partner.lang = this.translateService.currentLang;
        }

        return this.partnerService.createPartner(partner, password, sendEmail);
      }),
      switchMap((newPartner: Partner) => {
        if (!newPartner) {
          return of(false);
        }

        return this.loginStateService.login(newPartner.email, password, false).pipe(
          map(user => {
            if (user) {
              this._partner = newPartner;
            }
            return !!user;
          }),
        );
      }),
    );
  }

  registerPartnerAuthorization(partnerId: number, email: string, password: string): Observable<boolean> {
    return this.partnerService.registerPartnerAuthorization(partnerId, email, password).pipe(
      switchMap((newPartner: Partner) => {
        if (!newPartner) {
          return of(false);
        }

        return this.loginStateService.login(newPartner.email, password, false).pipe(
          map(user => {
            return !!user;
          }),
        );
      }),
    );
  }

  updatePartner(partner: Partner): Observable<Partner> {
    return this.partnerService.updatePartner(partner).pipe(
      map(
        (result: Partner) => {
          if (result) {
            this._partner = result;
          }
          return result;
        }));
  }

  private _reloadChildren(): Observable<boolean> {
    return of(false).pipe(
      switchMap(_ => {
        if (!this._partner) {
          return of(_);
        }

        return this.partnerService.getChildren().pipe(
          map(children => {
            if (this._partner) {
              this._partner.children = children;
            }
            return true;
          }),
        );
      })
    );
  }

  createChild(child: Partner): Observable<Partner> {
    return this.partnerService.createChild(child).pipe(
      switchMap(
        (result: Partner) => {
          return this._reloadChildren().pipe(map(_ => result));
        }));
  }

  updateChild(child: Partner): Observable<Partner> {
    return this.partnerService.updateChild(child).pipe(
      switchMap(
        (result: Partner) => {
          return this._reloadChildren().pipe(map(_ => result));
        }));
  }

  removeChild(child: Partner): Observable<boolean> {
    return this.partnerService.removeChild(child).pipe(
      switchMap(
        (result) => {
          return this._reloadChildren();
        }));
  }

  reset(): void {
    this._state = PartnerStateEnum.Reset;
    this.updateSelectedChild(null);
  }

  update(state: PartnerStateEnum,
    child?: Partner, orderId?: number): void {
    this._state = state;

    switch (this._state) {
      case PartnerStateEnum.ShowChildrenList:
        this._showChildrenListComponent();
        break;

      case PartnerStateEnum.AddChild:
        this._showAddChildComponent();
        break;

      case PartnerStateEnum.EditChild:
        this._showEditChildrenComponent(child);
        break;

      case PartnerStateEnum.ShowOrders:
        this._showOrdersComponent();
        break;

      case PartnerStateEnum.ShowOrder:
        this._showOrderComponent(orderId);
        break;

      case PartnerStateEnum.ShowPartnerProfile:
        this._showPartnerRegistrationComponent();
        break;

      case PartnerStateEnum.PartnerDelete:
        this._showPartnerDeleteComponent();
        break;
    }
  }

  private _showChildrenListComponent(): void {
    this.updateSelectedChild(null);
    this.router.navigateByUrl('/partner/children');
  }

  private _showAddChildComponent(): void {
    this.router.navigateByUrl('/partner/children/new');
  }

  private _showEditChildrenComponent(child: Partner): void {
    this.updateSelectedChild(child);
    this.router.navigateByUrl('/partner/children/edit');
  }

  private _showOrdersComponent(): void {
    this.router.navigateByUrl('/partner/orders');
  }

  private _showOrderComponent(orderId: number): void {
    this.router.navigateByUrl(`/partner/order/${orderId}`);
  }

  private _showPartnerRegistrationComponent(): void {
    this.router.navigateByUrl('/partner');
  }

  private _showPartnerDeleteComponent(): void {
    this.router.navigateByUrl('/partner/delete');
  }

  updateSelectedChild(child: Partner): void {
    this._selectedChild = child;
  }

  removeLoggedInPartner(password: string): Observable<boolean> {
    return this.partnerService.removeLoggedInPartner(password).pipe(
      switchMap(result => {
        if (!result) {
          return of(false);
        }

        if (!result.result) {
          return throwError(result.message);
        }

        return this.loginStateService.logout();
      }),
    );
  }

  uploadPhotoToField(photo: string, fieldToUpload: string): Observable<boolean> {
    return this.partnerService.uploadPhotoToField(photo, fieldToUpload).pipe(
      map(
        (result: boolean) => {
          return result;
        }));
  }

}
